import React, { useCallback, useEffect, useMemo, useState } from "react";
import { bool, func, shape } from "prop-types";
import { connect } from "react-redux";
import { compose } from "redux";
import { Redirect } from "react-router-dom";
import { push } from "react-router-redux";
import Yup from "yup";

import { CreateAccount } from "@ui-kit/components/index.es";
import { resizableRoute } from "pages/_components/Resizable";

import { actions as accountsActions, selectors as accountsSelectors } from "reducers/accounts";
import { selectors as sessionSelectors } from "reducers/session";
import { actions as transactionsActions } from "reducers/transactions";
import { actions as layoutActions } from "reducers/layout";
import { actions as statusActions } from "reducers/status";

import * as i18n from "util/i18n";
import { dispatchV3ShellLayout, isUxDashboardImprovementsEnabled } from "util/v3ShellLayoutSync";
import { getAccountsV3Label } from "v3/v3UiKitI18n";

const FORM_ID = "accounts.new.account";

function findOptionByValue(options, raw) {
    if (raw === undefined || raw === null || raw === "") {
        return null;
    }
    return options.find((o) => String(o.value) === String(raw)) ?? null;
}

const V3_ACCOUNTS_EDIT_PATH = "/v3/accounts/edit";
const V3_ACCOUNT_OPENING_SUMMARY_PATH = "/v3/accountOpening/summary";

function AccountCreateV3Page(props) {
    const { dispatch, openingData, isRetailEnvironment, location } = props;
    const [loadingSubmit, setLoadingSubmit] = useState(false);

    const isEditDraftRoute = location.pathname === V3_ACCOUNTS_EDIT_PATH;
    const isModifyFlow = Boolean(openingData?.returnToPending) || isEditDraftRoute;

    const goBackTargetPath = useMemo(() => {
        const fromList =
            openingData?.returnToPending &&
            typeof openingData?.draftTransactionListGoBackPath === "string" &&
            openingData.draftTransactionListGoBackPath.startsWith("/")
                ? openingData.draftTransactionListGoBackPath
                : null;
        return fromList || "/v3/accounts";
    }, [openingData?.returnToPending, openingData?.draftTransactionListGoBackPath]);

    const typeOptions = useMemo(() => {
        const specialAccount = isRetailEnvironment
            ? { value: 7, label: i18n.get("accounts.new.account.accountType.7") }
            : { value: 5, label: i18n.get("accounts.new.account.accountType.5") };
        return [specialAccount, { value: 3, label: i18n.get("accounts.new.account.accountType.3") }];
    }, [isRetailEnvironment]);

    const currencyOptions = useMemo(
        () => [
            { value: 0, label: i18n.get("accounts.new.account.currency.0") },
            { value: 2, label: i18n.get("accounts.new.account.currency.2") },
        ],
        [],
    );

    const initialValues = useMemo(() => {
        const od = openingData || {};
        const type =
            od.accountType !== undefined && od.accountType !== null && od.accountType !== ""
                ? findOptionByValue(typeOptions, od.accountType)
                : null;
        const currency =
            od.currency !== undefined && od.currency !== null && od.currency !== ""
                ? findOptionByValue(currencyOptions, od.currency)
                : null;
        const message = typeof od.message === "string" && od.message.length > 0 ? od.message : "";
        return {
            type,
            currency,
            message,
        };
    }, [openingData, typeOptions, currencyOptions]);

    const validationSchema = useMemo(
        () =>
            Yup.object().shape({
                type: Yup.mixed().required(i18n.get(`${FORM_ID}.field.error.required`)),
                currency: Yup.mixed().required(i18n.get(`${FORM_ID}.field.error.required`)),
                message: Yup.string()
                    .notRequired()
                    .test(
                        "message-format",
                        i18n.get("accounts.new.account.message.validation"),
                        (v) =>
                            v === undefined ||
                            v === null ||
                            String(v).trim() === "" ||
                            /^[a-zA-ZÀ-ÿ0-9_!@#$%&*.,() ]+$/.test(String(v)),
                    ),
            }),
        [],
    );

    const getFormLabel = useCallback((id) => {
        const map = {
            type: i18n.get(`${FORM_ID}.accountType.label`),
            typePlaceholder: i18n.get("accounts.new.account.accountType.placeholder", "Seleccioná"),
            currency: i18n.get(`${FORM_ID}.currency.label`),
            currencyPlaceholder: i18n.get("accounts.new.account.currency.placeholder", "Seleccioná"),
            message: i18n.get(`${FORM_ID}.message.label`),
            messagePlaceholder: i18n.get(`${FORM_ID}.message.placeholder`, ""),
            continue: i18n.get("global.continue"),
        };
        return map[id] ?? id;
    }, []);

    const handleGoBack = useCallback(() => {
        dispatch(push(goBackTargetPath));
    }, [dispatch, goBackTargetPath]);

    const setFormikProps = useCallback((formik) => {
        if (formik && typeof formik.isSubmitting === "boolean") {
            setLoadingSubmit(formik.isSubmitting);
        }
    }, []);

    const handleSubmit = useCallback(
        (values) => {
            const accountType = values?.type?.value ?? values?.type;
            const currency = values?.currency?.value ?? values?.currency;
            const message = values?.message ?? "";

            dispatch(
                accountsActions.accountOpeningData({
                    accountType,
                    currency,
                    message,
                    idTransaction: openingData?.idTransaction,
                }),
            );
            dispatch(push(V3_ACCOUNT_OPENING_SUMMARY_PATH));
        },
        [dispatch, openingData],
    );

    useEffect(() => {
        dispatch(transactionsActions.saveTransactionRoute(location.pathname));
        dispatch(statusActions.saveLastHref(location));
    }, [dispatch, location]);

    useEffect(() => {
        if (!isUxDashboardImprovementsEnabled()) {
            return undefined;
        }
        const title = isModifyFlow ? i18n.get("modify.account") : getAccountsV3Label("accounts.page.newAccount");
        dispatchV3ShellLayout(dispatch, {
            headerTitle: title,
            titleKey: "",
            goBackPath: goBackTargetPath,
            scopes: ["accounts", "accountOpening"],
        });
        return () => {
            dispatch(layoutActions.setScopes([]));
            dispatch(layoutActions.setTitleKey(""));
            dispatch(layoutActions.setHeaderTitle(""));
            dispatch(layoutActions.setHeaderSubtitle(""));
            dispatch(layoutActions.setGoBackPath(null));
        };
    }, [dispatch, isModifyFlow, goBackTargetPath]);

    const hintRaw = i18n.get(`${FORM_ID}.hint`, null);
    const hintText =
        typeof hintRaw === "string" && hintRaw.length > 0 && !hintRaw.startsWith("*") ? hintRaw : undefined;
    const pageTitle = isModifyFlow
        ? i18n.get("modify.account")
        : i18n.get("new.account", getAccountsV3Label("accounts.page.newAccount"));

    if (isEditDraftRoute && !openingData?.returnToPending) {
        return <Redirect to="/v3/accounts" />;
    }

    return (
        <CreateAccount
            key={
                openingData?.idTransaction
                    ? `${isEditDraftRoute ? "edit" : "create"}-${openingData.idTransaction}`
                    : "create-new"
            }
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            setFormikProps={setFormikProps}
            getFormLabel={getFormLabel}
            loading={false}
            loadingSubmit={loadingSubmit}
            typeOptions={typeOptions}
            currencyOptions={currencyOptions}
            goBack={handleGoBack}
            pageTitle={pageTitle}
            hintText={hintText}
        />
    );
}

AccountCreateV3Page.propTypes = {
    dispatch: func.isRequired,
    openingData: shape({}),
    isRetailEnvironment: bool.isRequired,
    location: shape({}).isRequired,
};

AccountCreateV3Page.defaultProps = {
    openingData: null,
};

const mapStateToProps = (state) => ({
    openingData: accountsSelectors.getAccountOpeningData(state) || null,
    isRetailEnvironment: sessionSelectors.isRetailEnvironment(state),
});

export default compose(connect(mapStateToProps), resizableRoute)(AccountCreateV3Page);
